import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Typography,
  Box
} from '@mui/material';
import {
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { ProblemAttemptDetail } from '../../types/teacher';

interface PerProblemAttemptTableProps {
  problemBreakdown: ProblemAttemptDetail[];
  studentName: string;
}

type OrderDirection = 'asc' | 'desc';
type OrderBy = 'problemTitle' | 'sessionTitle' | 'attemptCount' | 'pointsEarned' | 'submissionDate';

const PerProblemAttemptTable: React.FC<PerProblemAttemptTableProps> = ({
  problemBreakdown,
  studentName
}) => {
  const [orderBy, setOrderBy] = useState<OrderBy>('submissionDate');
  const [order, setOrder] = useState<OrderDirection>('desc');

  const handleSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = [...problemBreakdown].sort((a, b) => {
    let aValue: string | number | Date;
    let bValue: string | number | Date;

    switch (orderBy) {
      case 'problemTitle':
        aValue = a.problemTitle;
        bValue = b.problemTitle;
        break;
      case 'sessionTitle':
        aValue = a.sessionTitle;
        bValue = b.sessionTitle;
        break;
      case 'attemptCount':
        aValue = a.attemptCount;
        bValue = b.attemptCount;
        break;
      case 'pointsEarned':
        aValue = a.pointsEarned;
        bValue = b.pointsEarned;
        break;
      case 'submissionDate':
        aValue = new Date(a.submissionDate).getTime();
        bValue = new Date(b.submissionDate).getTime();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) {
      return order === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return order === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const getAttemptColor = (attempts: number): 'success' | 'warning' | 'error' => {
    if (attempts === 1) return 'success';
    if (attempts <= 3) return 'warning';
    return 'error';
  };

  const formatDate = (date: Date | string): string => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (problemBreakdown.length === 0) {
    return (
      <Card>
        <CardHeader
          title="Per-Problem Breakdown"
          subheader={`${studentName}'s attempt details`}
          avatar={<AssignmentIcon color="primary" />}
        />
        <CardContent>
          <Typography color="text.secondary">
            No problems solved yet. The breakdown will appear once the student starts solving problems.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Per-Problem Breakdown"
        subheader={`${studentName}'s attempt details (${problemBreakdown.length} problems solved)`}
        avatar={<AssignmentIcon color="primary" />}
      />
      <CardContent>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'problemTitle'}
                    direction={orderBy === 'problemTitle' ? order : 'asc'}
                    onClick={() => handleSort('problemTitle')}
                  >
                    Problem
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'sessionTitle'}
                    direction={orderBy === 'sessionTitle' ? order : 'asc'}
                    onClick={() => handleSort('sessionTitle')}
                  >
                    Session
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'attemptCount'}
                    direction={orderBy === 'attemptCount' ? order : 'asc'}
                    onClick={() => handleSort('attemptCount')}
                  >
                    Attempts
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'pointsEarned'}
                    direction={orderBy === 'pointsEarned' ? order : 'asc'}
                    onClick={() => handleSort('pointsEarned')}
                  >
                    Points
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'submissionDate'}
                    direction={orderBy === 'submissionDate' ? order : 'asc'}
                    onClick={() => handleSort('submissionDate')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((problem) => (
                <TableRow key={problem.problemId} hover>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                      {problem.problemTitle}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {problem.sessionTitle}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={problem.attemptCount}
                      color={getAttemptColor(problem.attemptCount)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="medium">
                      {problem.pointsEarned.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(problem.submissionDate)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            🟢 Green = First try • 🟡 Yellow = 2-3 attempts • 🔴 Red = 4+ attempts
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PerProblemAttemptTable;
